import /*React,*/ { useState, useEffect } from 'react';
import log from 'loglevel';
import { AuthState } from './types/app';
import { authService } from './services/authService';
import { AuthCallback } from './components/AuthCallback';
import { Login } from './components/Login';
import { Dashboard } from './components/Dashboard';
import './App.css';

// Initialize loglevel from environment
const logLevel = import.meta.env.VITE_LOG_LEVEL || 'info';
log.setLevel(logLevel);
const levelNames = Object.keys(log.levels).filter(
  key => typeof log.levels[key as keyof typeof log.levels] === 'number'
);
const currentLevelName = levelNames.find(
  name => log.levels[name as keyof typeof log.levels] === log.getLevel()
);
log.info('Logger initialized', {
  level: log.getLevel(),
  levelName: currentLevelName || logLevel,
  configuredFromEnv: logLevel,
});

function App() {
  const [authState, setAuthState] = useState<AuthState>(authService.getAuthState());
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Check if we're on the auth callback route
    if (window.location.pathname === '/auth/callback' && window.location.hash) {
      const newAuthState = authService.handleAuthCallback(window.location.hash);
      if (newAuthState) {
        setAuthState(newAuthState);
        // Clear the hash and redirect to home
        window.history.replaceState({}, document.title, '/');
      }
    }
    setIsLoading(false);
  }, []);

  const handleLogin = () => {
    authService.initiateAuth();
  };

  const handleLogout = () => {
    authService.logout();
    setAuthState(authService.getAuthState());
  };

  const updateAuthState = (newState: AuthState) => {
    setAuthState(newState);
  };

  if (isLoading) {
    return (
      <div className="app">
        <div className="loading">
          <h2>Loading...</h2>
        </div>
      </div>
    );
  }

  // Handle auth callback
  if (window.location.pathname === '/auth/callback') {
    return <AuthCallback onAuthComplete={updateAuthState} />;
  }

  return (
    <div className="app">
      <header className="app-header">
        {authState.isAuthenticated && (
          <button onClick={handleLogout} className="logout-button-top">
            Logout
          </button>
        )}
        <div className="header-content">
          <h1>Spendable</h1>
          <p>Track your YNAB budget spendable amounts</p>
        </div>
      </header>

      <main className="app-main">
        {authState.isAuthenticated ? (
          <Dashboard authState={authState} onAuthStateUpdate={updateAuthState} />
        ) : (
          <Login onLogin={handleLogin} />
        )}
      </main>
    </div>
  );
}

export default App;
